import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";

import {
    AdjustmentType,
    GetActiveOrderQuery,
} from "../../../common/generated-types";
import {
    getSaleableStockFromLevels,
    hasLineStockIssue as lineHasStockIssue,
} from "../../../common/utils/cart-stock.utils";
import { ConsolidatedFeedStorefrontService } from "../../../common/services/consolidated-feed-storefront.service";
import { NotificationService } from "../../../core/providers/notification/notification.service";
import { CartUploadService } from "../../../core/providers/cart-upload/cart-upload.service";
import { AVAILABILITY_TEXT } from "../../constants/availability.constants";
import { getOrderStep } from "../../utils/pricing.utils";
import { DateUtils } from "../../utils/date.utils";
import { inferAppliedVolumeDiscount } from "../../utils/volume-discount.utils";
import {
    cartHasConsolidatedFeedLines,
    isConsolidatedFeedLine,
    isConsolidatedFeedMovMet,
} from "../../../common/utils/consolidated-feed-cart";

import {
    CART_IMPORT_TEXT,
    CART_PROGRESS_COLOR_LOW,
    CART_PROGRESS_COLOR_READY,
    CART_PROGRESS_COLOR_WARNING,
    CONSOLIDATED_FEED_GROUP_ID,
    CONSOLIDATED_FEED_ORDER_TITLE,
} from "./cart-contents.constants";
import {
    CartLine,
    CartLineDiscount,
    ExtendedCartLine,
    ExtendedProductVariant,
    LinePromotionSummary,
    QuantityChangePayload,
    SupplierGroup,
} from "./cart-contents.types";

@Component({
    selector: "vsf-cart-contents",
    templateUrl: "./cart-contents.component.html",
    styleUrls: ["./cart-contents.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartContentsComponent implements OnChanges, OnDestroy {
    readonly TEXT = AVAILABILITY_TEXT;
    readonly IMPORT_TEXT = CART_IMPORT_TEXT;
    @Input() cart: GetActiveOrderQuery["activeOrder"];
    @Input() isOrderDetail = false;
    @Input() canAdjustQuantities = false;
    @Input() canEdit = false;
    @Input() isImporting = false;
    @Input() isEmpty = false;
    @Input() failedProductGtins: string[] = [];
    @Input() set moqViolations(
        violations: Array<{ gtin: string; moq: number; quantity: number }>,
    ) {
        this.moqViolationsList = violations || [];
        this._moqViolationsMap = new Map(
            this.moqViolationsList.map((v) => [v.gtin, v.moq]),
        );
    }

    moqViolationsList: Array<{
        gtin: string;
        moq: number;
        quantity: number;
    }> = [];

    @Output() checkoutReadinessChange = new EventEmitter<boolean>();
    @Output() removeItemEvent = new EventEmitter<string>();
    @Output() setQuantity = new EventEmitter<QuantityChangePayload>();
    @Output() removeAllItemsEvent = new EventEmitter<void>();
    @Output() includedChange = new EventEmitter<string[]>();

    supplierGroups: SupplierGroup[] = [];
    _moqViolationsMap = new Map<string, number>();
    hasMoqViolations = false;

    private readonly destroy$ = new Subject<void>();
    private readonly quantityChangeSubject = new Subject<QuantityChangePayload>();
    private readonly openGroupIds = new Set<string>();
    private readonly excludedGroupIds = new Set<string>();
    private groupsInitialized = false;

    expandedLineId: string | null = null;

    isCartActionsOpen = false;
    isModalVisible = false;

    constructor(
        private cd: ChangeDetectorRef,
        private notificationService: NotificationService,
        private cartUploadService: CartUploadService,
        private consolidatedFeedStorefront: ConsolidatedFeedStorefrontService,
    ) {
        this.consolidatedFeedStorefront.ensureLoaded().subscribe(() => {
            if (this.cart) {
                this.groupLinesBySupplier();
            }
            this.cd.markForCheck();
        });
        this.quantityChangeSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(
                    (prev, curr) =>
                        prev.itemId === curr.itemId &&
                        prev.quantity === curr.quantity
                ),
                takeUntil(this.destroy$)
            )
            .subscribe(({ itemId, quantity }) => {
                this.setQuantity.emit({ itemId, quantity });
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.cart && this.cart) {
            this.groupLinesBySupplier();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.quantityChangeSubject.complete();
    }

    groupLinesBySupplier(): void {
        if (!this.cart || !this.cart.lines) {
            this.supplierGroups = [];
            this.checkoutReadinessChange.emit(false);
            return;
        }

        const consolidatedLines: ExtendedCartLine[] = [];
        const regularLines: ExtendedCartLine[] = [];

        for (const line of this.cart.lines) {
            const extendedLine = line as unknown as ExtendedCartLine;
            if (isConsolidatedFeedLine(extendedLine)) {
                consolidatedLines.push(extendedLine);
            } else {
                regularLines.push(extendedLine);
            }
        }

        const groupedLines = regularLines.reduce((groups, extendedLine) => {
            const supplierId = this.resolveLineSupplierId(extendedLine);
            const supplierName =
                extendedLine.productVariant.sku ?? "Unknown Supplier";

            if (!groups[supplierId]) {
                groups[supplierId] = {
                    supplierId,
                    supplierName,
                    lines: [],
                    totalValue: 0,
                    mov: extendedLine.productVariant.pricing?.mov ?? 0,
                    progressPercentage: 0,
                    isReadyForCheckout: false,
                    promotionDescription:
                        extendedLine.productVariant.pricing
                            ?.promotionDescription ?? "",
                    promotionTextColor:
                        extendedLine.productVariant.pricing
                            ?.promotionTextColor ?? "",
                    supplyType:
                        extendedLine.productVariant.pricing?.supplyType ?? "",
                    leadTimeFrom:
                        extendedLine.productVariant.pricing?.leadTimeFrom ?? 0,
                    leadTimeTo:
                        extendedLine.productVariant.pricing?.leadTimeTo ?? 0,
                    prePaymentPercentage:
                        extendedLine.productVariant.pricing
                            ?.prePaymentPercentage ?? 0,
                    preOrderShippingDate:
                        extendedLine.productVariant.pricing
                            ?.preOrderShippingDate ?? "",
                    fullPackageOnly:
                        extendedLine.productVariant.pricing
                            ?.fullPackageOnly ?? false,
                    shippingDate:
                        extendedLine.productVariant.pricing?.shippingDate ?? "",
                    volumeDiscounts:
                        extendedLine.productVariant.pricing?.volumeDiscounts,
                    volumeDiscountPercentApplied: null,
                };
            }
            groups[supplierId].lines.push(extendedLine);
            groups[supplierId].totalValue +=
                extendedLine.discountedLinePriceWithTax ??
                extendedLine.linePriceWithTax;

            return groups;
        }, {} as { [supplierId: string]: SupplierGroup });

        Object.values(groupedLines).forEach((group) => {
            this.applySupplierGroupCheckoutState(group);
        });

        this.supplierGroups = Object.values(groupedLines);
        if (consolidatedLines.length > 0) {
            const consolidatedGroup =
                this.buildConsolidatedFeedGroup(consolidatedLines);
            this.supplierGroups = [consolidatedGroup, ...this.supplierGroups];
        }

        if (!this.groupsInitialized) {
            for (const group of this.supplierGroups) {
                if (!group.isReadyForCheckout) {
                    this.openGroupIds.add(group.supplierId);
                }
            }
            if (this.openGroupIds.size === 0 && this.supplierGroups[0]) {
                this.openGroupIds.add(this.supplierGroups[0].supplierId);
            }
            this.groupsInitialized = true;
        }

        const allGroupsReady = this.supplierGroups
            .filter((group) => this.isGroupIncluded(group))
            .every((group) => group.isReadyForCheckout);

        const hasStockProblems = this.cart.lines.some((line) =>
            this.hasStockLineIssue(line)
        );

        const hasMoqProblems = regularLines.some((line) =>
            this.hasMoqViolation(line),
        );
        this.hasMoqViolations = hasMoqProblems;

        const isReadyForCheckout = allGroupsReady && !hasStockProblems && !hasMoqProblems;

        this.checkoutReadinessChange.emit(isReadyForCheckout);
    }

    getMoq = (line: CartLine | ExtendedCartLine): number => {
        const pv = line.productVariant as ExtendedProductVariant;
        return pv.moq ?? 1;
    };

    hasMoqViolation = (line: CartLine | ExtendedCartLine): boolean => {
        const moq = this.getMoq(line);
        return line.quantity < moq;
    };

    getViolatedMoq = (line: CartLine): number => {
        return this.getMoq(line);
    };

    private showMoqAlert = (moq: number): void => {
        this.notificationService
            .error(`${AVAILABILITY_TEXT.MOQ_ALERT} ${moq}`)
            .subscribe();
    };

    increment = (item: CartLine): void => {
        const pv = item.productVariant as ExtendedProductVariant;
        const maxQ = this.getMaxQ(item);
        const minQ = this.getMinQ(item);

        const incrementStep = getOrderStep(pv, {
            customFields: {
                quantityPerBox: pv?.product?.customFields?.quantityPerBox ?? null,
            },
        });
        const newQuantity = item.quantity + incrementStep;

        if (maxQ && newQuantity > maxQ) {
            return;
        }

        this.setQuantity.emit({ itemId: item.id, quantity: newQuantity });
    };

    decrement = (item: CartLine): void => {
        const pv = item.productVariant as ExtendedProductVariant;
        const minQ = this.getMinQ(item);
        const moq = this.getMoq(item);

        const decrementStep = getOrderStep(pv, {
            customFields: {
                quantityPerBox: pv?.product?.customFields?.quantityPerBox ?? null,
            },
        });
        const newQuantity = item.quantity - decrementStep;

        if (newQuantity < moq && newQuantity > 0) {
            this.showMoqAlert(moq);
        }

        if (newQuantity >= 0) {
            this.setQuantity.emit({ itemId: item.id, quantity: newQuantity });
        }
    };

    trackByFn = (_index: number, line: { id: string }): string => line.id;

    trackBySupplierGroup = (_index: number, group: SupplierGroup): string =>
        group.supplierId;

    removeItem = (line: CartLine): void => {
        this.removeItemEvent.emit(line.id);
    };

    removeAllItems = (): void => {
        this.removeAllItemsEvent.emit();
    };

    handlePopupKeydown = (event: KeyboardEvent): void => {
        if (event.key === "Escape") {
            this.isCartActionsOpen = false;
        }
    };

    openUploadModal = (): void => {
        this.isModalVisible = true;
        this.cd.markForCheck();
    };

    exportCart = (): void => {
        this.cartUploadService.exportCartToXlsx(this.cart?.lines ?? []);
        this.isCartActionsOpen = false;
        this.cd.markForCheck();
    };

    closeUploadModal = (): void => {
        this.isModalVisible = false;
        this.cd.markForCheck();
    };

    onUploadSuccess = (result: {
        successfulCount: number;
        failedProductGtins: string[];
        moqViolations: Array<{ gtin: string; moq: number; quantity: number }>;
    }): void => {
        this.failedProductGtins = result.failedProductGtins;
        this.moqViolations = result.moqViolations ?? [];
        this.cd.markForCheck();
        this.closeUploadModal();
    };

    getProgressColor = (group: SupplierGroup): string => {
        if (group.progressPercentage >= 100) {
            return CART_PROGRESS_COLOR_READY;
        }
        if (group.progressPercentage >= 50) {
            return CART_PROGRESS_COLOR_WARNING;
        }
        return CART_PROGRESS_COLOR_LOW;
    };

    getBarTrackColor(group: SupplierGroup): string {
        return group.isReadyForCheckout ? "#d8f0e2" : "#eee3dc";
    }

    getCompactStatusColor(group: SupplierGroup): string {
        return group.isReadyForCheckout ? "#2f8557" : "#c0512b";
    }

    getCompactStatusLine(group: SupplierGroup): string {
        if (group.isReadyForCheckout) {
            return "MOV met";
        }
        const remaining = this.getAmountRemaining(group);
        return `${this.formatEuroHint(remaining)} below MOV`;
    }

    getSupplyTypeKey(group: SupplierGroup): string {
        const t = (group.supplyType || "").toLowerCase();
        if (t.includes("pre")) return "preorder";
        if (t.includes("short") || t.includes("hot")) return "hot";
        return "inventory";
    }

    getSupplyTypeLabel(group: SupplierGroup): string {
        const key = this.getSupplyTypeKey(group);
        if (key === "preorder") return "Pre-order";
        if (key === "hot") return "Hot offer";
        return "Inventory";
    }

    isGroupOpen(group: SupplierGroup): boolean {
        return this.openGroupIds.has(group.supplierId);
    }

    toggleGroupOpen(group: SupplierGroup): void {
        if (this.openGroupIds.has(group.supplierId)) {
            this.openGroupIds.delete(group.supplierId);
        } else {
            this.openGroupIds.add(group.supplierId);
        }
        this.cd.markForCheck();
    }

    isGroupIncluded(group: SupplierGroup): boolean {
        return !this.excludedGroupIds.has(group.supplierId);
    }

    toggleIncluded(group: SupplierGroup): void {
        if (this.excludedGroupIds.has(group.supplierId)) {
            this.excludedGroupIds.delete(group.supplierId);
        } else {
            this.excludedGroupIds.add(group.supplierId);
        }
        this.includedChange.emit([...this.excludedGroupIds]);
        this.cd.markForCheck();
    }

    getUndiscountedUnitPrice(line: CartLine): number {
        if (!line.quantity) {
            return line.unitPriceWithTax;
        }
        return Math.round(line.linePriceWithTax / line.quantity);
    }

    getBundleLabel(line: CartLine): string | null {
        const pv = line.productVariant as ExtendedProductVariant;
        const perBox = pv?.product?.customFields?.quantityPerBox;
        if (perBox != null && perBox > 1) {
            return `${perBox}/box`;
        }
        const moq = this.getMoq(line);
        if (moq > 1) {
            return `MOQ ${moq}`;
        }
        return null;
    }

    private formatEuroHint(cents: number): string {
        return new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(cents / 100);
    }

    onQuantityChange = (line: CartLine, event: Event): void => {
        const input = event.target as HTMLInputElement;
        const newQuantity = parseInt(input.value, 10);
        const moq = this.getMoq(line);

        if (newQuantity < moq && newQuantity > 0) {
            this.showMoqAlert(moq);
        }

        this.quantityChangeSubject.next({
            itemId: line.id,
            quantity: newQuantity >= 0 ? newQuantity : 0,
        });
    };

    isConsolidatedFeedCart(): boolean {
        return cartHasConsolidatedFeedLines(this.cart?.lines);
    }

    isConsolidatedFeedGroup(group: SupplierGroup): boolean {
        return group.supplierId === CONSOLIDATED_FEED_GROUP_ID;
    }

    getAmountRemaining = (group: SupplierGroup): number =>
        Math.max(0, group.mov - group.totalValue);

    getSubtotal = (): number => this.cart?.totalWithTax ?? 0;

    getReadyForCheckoutCount = (): number =>
        this.supplierGroups.filter((group) => group.isReadyForCheckout).length;

    getBelowMinimumCount = (): number =>
        this.supplierGroups.filter((group) => !group.isReadyForCheckout).length;

    isDiscounted = (line: CartLine): boolean =>
        line.discountedLinePriceWithTax < line.linePriceWithTax;

    getLinePromotions = (
        adjustments: CartLineDiscount[]
    ): LinePromotionSummary[] => {
        const groupedPromotions = adjustments
            .filter((a) => a.type === AdjustmentType.PROMOTION)
            .reduce((groups, promotion) => {
                groups[promotion.description] =
                    (groups[promotion.description] ?? 0) + promotion.amount;
                return groups;
            }, {} as { [description: string]: number });
        return Object.entries(groupedPromotions).map(([key, value]) => ({
            description: key,
            amount: value,
        }));
    };

    getAvailableStock = (line: CartLine): number => {
        if (this.isInvalidProduct(line)) {
            return 0;
        }
        const pv = line.productVariant as ExtendedProductVariant;
        return getSaleableStockFromLevels(pv.stockLevels);
    };

    getBrand = (line: CartLine): string => {
        const pv = line.productVariant as ExtendedProductVariant;
        const brandFacetValue = pv.facetValues?.find(
            (fv) => fv.facet.code === "brand"
        );
        return brandFacetValue ? brandFacetValue.name : "";
    };

    getPrimaryFacetName = (line: CartLine): string => {
        const pv = line.productVariant as ExtendedProductVariant;
        return pv.facetValues?.[0]?.name ?? "";
    };

    hasStockLineIssue = (line: CartLine): boolean =>
        lineHasStockIssue({
            quantity: line.quantity,
            productVariant: {
                isDeleted: (line.productVariant as ExtendedProductVariant)
                    .isDeleted,
                stockLevels: (line.productVariant as ExtendedProductVariant)
                    .stockLevels,
            },
        });

    isOutOfStock = (line: CartLine): boolean =>
        this.getAvailableStock(line) <= 0;

    getStockDisplayText = (line: CartLine): string => {
        if (this.isInvalidProduct(line)) {
            return "Out Of Stock";
        }

        const pv = line.productVariant as ExtendedProductVariant;
        const isPreOrder = pv?.pricing?.supplyType === "pre-order";
        const saleable = this.getAvailableStock(line);

        if (line.quantity > saleable) {
            return `Insufficient stock: ${saleable} available, ${line.quantity} in cart`;
        }

        if (isPreOrder) {
            return "";
        }

        return this.isOutOfStock(line)
            ? "Out Of Stock"
            : `${saleable} Available`;
    };

    private isInvalidProduct = (line: CartLine): boolean => {
        const pv = line.productVariant as ExtendedProductVariant;
        return !!pv?.isDeleted;
    };

    private resolveLineSupplierId(line: ExtendedCartLine): string {
        const pricingId = line.productVariant.pricing?.id;
        if (pricingId) {
            return String(pricingId);
        }
        const supplierId = line.customFields?.supplierId;
        if (supplierId) {
            return String(supplierId);
        }
        return "unknown";
    }

    isPreOrder = (group: SupplierGroup): boolean =>
        group.supplyType?.toLowerCase() ===
        AVAILABILITY_TEXT.PRE_ORDER.toLowerCase();

    getLeadTimeText = (group: SupplierGroup): string => {
        if (group.preOrderShippingDate) {
            return group.preOrderShippingDate;
        }
        if (group.shippingDate) {
            return DateUtils.formatShippingDate(group.shippingDate);
        }
        return "";
    };

    getSupplierTitle = (group: SupplierGroup): string => {
        if (this.isConsolidatedFeedGroup(group)) {
            return CONSOLIDATED_FEED_ORDER_TITLE;
        }
        return this.isPreOrder(group)
            ? `Pre-Order Pricelist #${group.supplierId}`
            : `Supplier #${group.supplierId}`;
    };

    private buildConsolidatedFeedGroup(
        lines: ExtendedCartLine[],
    ): SupplierGroup {
        const totalValue = lines.reduce(
            (sum, line) =>
                sum +
                (line.discountedLinePriceWithTax ?? line.linePriceWithTax),
            0,
        );
        const group: SupplierGroup = {
            supplierId: CONSOLIDATED_FEED_GROUP_ID,
            supplierName: CONSOLIDATED_FEED_ORDER_TITLE,
            lines,
            totalValue,
            mov: this.consolidatedFeedStorefront.getCheckoutMovCents(),
            progressPercentage: 0,
            isReadyForCheckout: false,
            promotionDescription: "",
            promotionTextColor: "",
            supplyType: "",
            leadTimeFrom: 0,
            leadTimeTo: 0,
            prePaymentPercentage: 0,
            preOrderShippingDate: "",
            fullPackageOnly: false,
            shippingDate: "",
            volumeDiscounts: undefined,
            volumeDiscountPercentApplied: null,
        };
        group.isReadyForCheckout = isConsolidatedFeedMovMet(
            lines,
            this.consolidatedFeedStorefront.getCheckoutMovCents(),
        );
        group.progressPercentage = group.isReadyForCheckout ? 100 : 0;
        return group;
    }

    private applySupplierGroupCheckoutState(group: SupplierGroup): void {
        if (group.mov > 0) {
            group.progressPercentage = Math.min(
                (group.totalValue / group.mov) * 100,
                100,
            );
            group.isReadyForCheckout = group.progressPercentage >= 100;
        } else {
            group.progressPercentage = 100;
            group.isReadyForCheckout = true;
        }
        let appliedPercent: number | null = null;
        for (const line of group.lines) {
            for (const discount of line.discounts ?? []) {
                const match = String(discount.description ?? "").match(
                    /volume discount\s+([\d.]+)\s*%/i,
                );
                if (match) {
                    appliedPercent = Number(match[1]);
                    break;
                }
            }
            if (appliedPercent != null) break;
        }
        if (appliedPercent == null) {
            appliedPercent =
                inferAppliedVolumeDiscount(
                    group.totalValue,
                    group.volumeDiscounts,
                )?.discountPercent ?? null;
        }
        group.volumeDiscountPercentApplied = appliedPercent;
    }

    getMinQ = (line: CartLine): number => {
        const pv = line.productVariant as ExtendedProductVariant;
        return pv?.pricingProduct?.minQ ?? pv?.moq ?? 1;
    };

    getMaxQ = (line: CartLine): number | null => {
        const pv = line.productVariant as ExtendedProductVariant;
        return pv?.pricingProduct?.maxQ ?? null;
    };

    hasMinQ = (line: CartLine): boolean => {
        const pv = line.productVariant as ExtendedProductVariant;
        return !!pv?.pricingProduct?.minQ;
    };

    isLinePreOrder = (line: CartLine): boolean => {
        const pv = line.productVariant as ExtendedProductVariant;
        return pv?.pricing?.supplyType?.toLowerCase() === "pre-order";
    };
}
